import { memo, useMemo } from "react";
import { Box, Button, SxProps, Theme } from "@mui/material";
import Tooltip from "../Tooltip";

export type ToggleOption<T = string | number> = {
  value: T;
  label: string;
  tooltip?: string;
  disabled?: boolean;
};

export type TogglePillGroupProps<T = string | number> = {
  options: ToggleOption<T>[];
  selectedIndex?: number;
  selectedValue?: T;
  onChange: (option: ToggleOption<T>, index: number) => void;
  size?: "small" | "medium" | "large";
  disabled?: boolean;
  gap?: string | number;
  className?: string;
  sx?: SxProps<Theme>;
};

function TogglePillGroupBase<T = string | number>({
  options,
  selectedIndex,
  selectedValue,
  onChange,
  size = "small",
  disabled = false,
  gap = "4px",
  className,
  sx,
}: TogglePillGroupProps<T>) {
  const activeIndex = useMemo(() => {
    if (selectedValue !== undefined) {
      return options.findIndex((o) => o.value === selectedValue);
    }
    if (typeof selectedIndex === "number") return selectedIndex;
    return -1;
  }, [options, selectedIndex, selectedValue]);

  return (
    <Box
      display="flex"
      alignItems="center"
      flexWrap="wrap"
      gap={gap}
      className={className}
      sx={sx}
    >
      {options.map((opt, idx) => {
        const isActive = idx === activeIndex;
        const btn = (
          <Button
            key={
              (typeof opt.value === "string" ? opt.value : String(opt.value)) +
              String(idx)
            }
            size={size}
            variant={isActive ? "contained" : "outlined"}
            aria-pressed={isActive}
            disabled={disabled || opt.disabled}
            onClick={() => onChange(opt, idx)}
          >
            {opt.label}
          </Button>
        );
        return opt.tooltip ? (
          <Tooltip title={opt.tooltip} key={`tt-${idx}`}>
            {btn}
          </Tooltip>
        ) : (
          btn
        );
      })}
    </Box>
  );
}

export const TogglePillGroup = memo(
  TogglePillGroupBase,
) as typeof TogglePillGroupBase;

export default TogglePillGroup;
